import { PROVIDER_SEND_TURN_MAX_ATTACHMENTS } from "@afar-research/contracts";

/**
 * The Brain work an Action does: Ingest, or the Lint and Delete housekeeping.
 * Querying the Brain is not one of them: every thread can ask it anything.
 */
export type BrainAction = "ingest" | "lint" | "delete";

/**
 * How a Brain Action presents itself and what it collects. One descriptor per
 * Action, so the composer chip, its blurb, and its text-disabled description
 * read from a single list rather than three parallel switch statements in the UI.
 */
export interface BrainActionDescriptor {
  readonly id: BrainAction;
  readonly label: string;
  /** What the Action does, said as an action rather than a name. */
  readonly headline: string;
  /** One line under the chip, dropped once the composer is too narrow to spare it. */
  readonly blurb: string;
  /** What the Action collects besides text. Drives the composer's chip row. */
  readonly collects: "sources" | "targets" | "none";
  /**
   * Lint and Delete take no typed input and show a description in place of the
   * input — never a greyed-out textarea. Removing the chip is the way out.
   */
  readonly typing:
    | { readonly kind: "enabled"; readonly placeholder: string }
    | { readonly kind: "disabled"; readonly description: string };
}

const BRAIN_ACTIONS: Readonly<Record<BrainAction, BrainActionDescriptor>> = {
  ingest: {
    id: "ingest",
    label: "Ingest",
    headline: "Ingest sources into the Brain",
    blurb: "Files, folders, and pasted text become raw sources, then wiki pages.",
    collects: "sources",
    typing: {
      kind: "enabled",
      placeholder: "Anything the ingest should pay attention to (optional)…",
    },
  },
  lint: {
    id: "lint",
    label: "Lint",
    headline: "Lint and repair the wiki",
    blurb: "Repairs the whole wiki. Takes no input.",
    collects: "none",
    typing: {
      kind: "disabled",
      description:
        "Walks every wiki page, repairs broken citations and stale links, and reports what it changed. Nothing to type — send when you're ready.",
    },
  },
  delete: {
    id: "delete",
    label: "Delete",
    headline: "Delete raw sources",
    blurb: "Destructive. Removes raws and repairs the citing wiki.",
    collects: "targets",
    typing: {
      kind: "disabled",
      description:
        "Removes the selected raw sources from the vault, then repairs every wiki page that cited them. Nothing to type — send when you're ready.",
    },
  },
};

export function describeBrainAction(action: BrainAction): BrainActionDescriptor {
  return BRAIN_ACTIONS[action];
}

/**
 * Whether a turn sent with this Brain Action carries what the composer holds.
 *
 * Lint and Delete take no typed input: the composer shows a description where
 * its editor would be and refuses attachments, so the turn has to carry
 * neither. Their whole submission is the Action itself. Without a Brain Action
 * the composer is the ordinary one and carries everything.
 */
export function brainActionCarriesComposerInput(action: BrainAction | null): boolean {
  return action === null || describeBrainAction(action).typing.kind === "enabled";
}

/**
 * Whether the composer can send with this Brain Action. Lint sends on the
 * Action alone; Ingest and Delete send on what their chips gathered, so an
 * empty selection is not a send.
 */
export function brainActionCanSend(input: {
  readonly action: BrainAction;
  /** Ingest sources or Delete targets the composer has gathered. */
  readonly collectedCount: number;
}): boolean {
  const { collects } = describeBrainAction(input.action);
  if (collects === "none") return true;
  return input.collectedCount > 0;
}

/**
 * How many attachments one composer send may carry. An Ingest send names every
 * source by a path the agent reads, so it has no cap; every other send holds to
 * the provider's per-turn attachment limit.
 */
export function composerAttachmentLimit(action: BrainAction | null): number {
  return action === "ingest" ? Number.POSITIVE_INFINITY : PROVIDER_SEND_TURN_MAX_ATTACHMENTS;
}

/**
 * Whether a composer send takes its files exactly as given. An Ingest send's
 * files are Project References the agent reads by path: they have no size
 * limit and an image is ingested as the original, not downscaled to fit a
 * provider's image input. Every other send keeps the provider's limits.
 */
export function composerTakesSourcesAsIs(action: BrainAction | null): boolean {
  return action === "ingest";
}
